import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { ReactComponent as UserIcon } from 'assets/user-black.svg';
import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots.svg';
import ClientAvatar from 'components/shared/ClientAvatar';

import { formatDealAmount } from 'helper/short-currency';

import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';
import { muiStyles } from './muiStyles';
import styles from './styles.module.css';
import { getCardStatus } from './utils';

const DealCard = ({
  data,
  onDrag,
  onSettingsClick,
}: {
  data: any;
  onDrag: (e: React.DragEvent, dealTitle: string) => void;
  onSettingsClick: (event: MouseEvent<SVGSVGElement>, deal: any) => void;
}) => {
  const {
    dealType,
    dealId,
    name,
    amount,
    owner,
    business,
    dealHealth,
    daysInStage,
    velocityTargetDate = 5,
  } = data;

  const avatar = data.client?.avatar ?? '';
  const clientName = data.client?.clientName ?? '';

  const { dotColor, tooltipText } = getCardStatus(dealHealth, velocityTargetDate);

  return (
    <>
      <Link to={`${RoutePaths.Deals}/${dealId}`} className={styles['card-link']}>
        <Box sx={muiStyles.card} draggable='true' onDragStart={(e) => onDrag(e, data.name)}>
          <Box sx={muiStyles.cardBody}>
            <Box sx={muiStyles.header}>
              <Typography variant='responsiveHeading4' sx={muiStyles.title}>
                {name}
              </Typography>

              <Box sx={muiStyles.optionsMenu}>
                <VerticalDotsIcon
                  className={styles['vertical-icon']}
                  onClick={(e: MouseEvent<SVGSVGElement>) => {
                    e.preventDefault();
                    onSettingsClick(e, data);
                  }}
                />
              </Box>
            </Box>

            <Box sx={muiStyles.clientCompanyContainer}>
              <ClientAvatar
                profilePicture={avatar ?? ''}
                clientName={clientName ?? 'N/A'}
                sx={muiStyles.clientAvatar}
              />
              <Typography variant='responsiveBody3'>{clientName ?? 'N/A'}</Typography>
              <Box sx={muiStyles.dotDivider} />
              <Tooltip
                sx={muiStyles.tooltip}
                title={<Typography variant='responsiveBody3'>{business?.name}</Typography>}
              >
                <Typography variant='responsiveBody3'>{business?.name}</Typography>
              </Tooltip>
            </Box>

            <Box sx={muiStyles.typeValueContainer}>
              <Chip
                label={<Typography variant='responsiveLabel1'>{dealType?.dealTypeName}</Typography>}
                sx={muiStyles.chip}
              />
              <Typography variant='responsiveHeading4'>{formatDealAmount(amount)}</Typography>
            </Box>
          </Box>

          <Divider sx={muiStyles.divider} />

          <Box sx={muiStyles.metaSection}>
            <Box sx={muiStyles.userInfo}>
              <UserIcon width={'18px'} height={'18px'} />
              <Typography variant='responsiveBody3'>{owner?.fullName}</Typography>
            </Box>
            <Box sx={muiStyles.statusIndicator}>
              <Tooltip
                title={
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral1 }}>
                    {tooltipText}
                  </Typography>
                }
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: zeplinColor.BasicBlack,
                      padding: '10px 16px',
                      borderRadius: '8px',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    ...muiStyles.dot,
                    backgroundColor: dotColor,
                  }}
                />
              </Tooltip>
              <Typography variant='responsiveBody3'>{daysInStage}d</Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    </>
  );
};

export default DealCard;
